/**
 * Validaciones (§8).
 *
 * La frase que gobierna este archivo: **un valor fuera de rango es un error de lectura, no
 * un récord. Nunca guardar en silencio.**
 *
 * Hay tres severidades, y la distinción importa:
 * - `error`    → no se guarda. Es físicamente imposible o seguro que se leyó mal.
 * - `confirmar`→ se guarda sólo si el usuario lo confirma explícitamente. Raro pero posible.
 * - `aviso`    → se guarda, y se le cuenta al usuario.
 */

import type { MedidaCorporal, Maquina, RegistroCardio, Serie } from './types.ts';
import { enUnidad } from './units.ts';

export type Severidad = 'error' | 'confirmar' | 'aviso';

export interface Hallazgo {
  campo: string;
  severidad: Severidad;
  codigo: string;
  mensaje: string;
}

export interface Resultado {
  /** true si no hay ningún `error`. Puede ser true y aun así requerir confirmación. */
  ok: boolean;
  requiereConfirmacion: boolean;
  hallazgos: Hallazgo[];
}

function resultado(hallazgos: Hallazgo[]): Resultado {
  return {
    ok: !hallazgos.some((h) => h.severidad === 'error'),
    requiereConfirmacion: hallazgos.some((h) => h.severidad === 'confirmar'),
    hallazgos,
  };
}

export function combinar(...rs: Resultado[]): Resultado {
  return resultado(rs.flatMap((r) => r.hallazgos));
}

// ---------------------------------------------------------------------------
// Límites de §8
// ---------------------------------------------------------------------------

export const LIMITES = {
  reps: { min: 1, max: 100 },
  grasaPct: { min: 3, max: 70 },
  /** Variación de peso corporal respecto de la lectura anterior que exige confirmación. */
  deltaPesoKg: 3,
  /** Por encima de esto la lectura de la consola es un error, no un entrenamiento. */
  caloriasPorMinuto: 25,
  /** Velocidad resultante máxima plausible en una máquina de cardio. */
  velocidadKmhMax: 45,
  /** Tolerancia de la identidad masa magra + masa grasa = peso. */
  toleranciaComposicion: 0.02,
  aguaPct: { min: 20, max: 80 },
  grasaVisceral: { min: 1, max: 60 },
  imc: { min: 8, max: 80 },
  tmb: { min: 500, max: 5000 },
  pesoCorporalKg: { min: 20, max: 400 },
} as const;

// ---------------------------------------------------------------------------
// Series
// ---------------------------------------------------------------------------

export function validarSerie(s: Pick<Serie, 'reps' | 'peso' | 'unidad'>, maquina?: Maquina | null): Resultado {
  const h: Hallazgo[] = [];

  if (!Number.isFinite(s.reps) || !Number.isInteger(s.reps)) {
    h.push({ campo: 'reps', severidad: 'error', codigo: 'reps_no_entero', mensaje: 'Las reps tienen que ser un número entero.' });
  } else if (s.reps < LIMITES.reps.min || s.reps > LIMITES.reps.max) {
    h.push({
      campo: 'reps',
      severidad: 'error',
      codigo: 'reps_fuera_de_rango',
      mensaje: `Reps fuera de rango: ${s.reps}. Se aceptan entre ${LIMITES.reps.min} y ${LIMITES.reps.max}.`,
    });
  }

  if (!Number.isFinite(s.peso) || s.peso < 0) {
    h.push({ campo: 'peso', severidad: 'error', codigo: 'peso_invalido', mensaje: 'El peso no puede ser negativo.' });
    return resultado(h);
  }

  if (maquina) {
    // El rango físico de la máquina está expresado en la unidad de su placa.
    const pesoEnPlaca = enUnidad({ valor: s.peso, unidad: s.unidad }, maquina.unidad_placa);
    const { placa_min, placa_max, unidad_placa, incremento_placa } = maquina;

    if (placa_min !== null && pesoEnPlaca < placa_min - 1e-6) {
      h.push({
        campo: 'peso',
        severidad: 'error',
        codigo: 'placa_bajo_minimo',
        mensaje: `Esa máquina arranca en ${placa_min} ${unidad_placa}.`,
      });
    }
    if (placa_max !== null && pesoEnPlaca > placa_max + 1e-6) {
      h.push({
        campo: 'peso',
        severidad: 'error',
        codigo: 'placa_sobre_maximo',
        mensaje: `Esa máquina llega hasta ${placa_max} ${unidad_placa}.`,
      });
    }
    // No es error: se puede haber agregado un disco encima. Pero conviene decirlo, porque
    // la causa habitual es un dígito de más.
    if (incremento_placa > 0 && Math.abs(pesoEnPlaca / incremento_placa - Math.round(pesoEnPlaca / incremento_placa)) > 1e-6) {
      h.push({
        campo: 'peso',
        severidad: 'aviso',
        codigo: 'fuera_de_incremento',
        mensaje: `Esa máquina sube de ${incremento_placa} en ${incremento_placa} ${unidad_placa}: ${pesoEnPlaca} no cae en una placa.`,
      });
    }
  }

  return resultado(h);
}

// ---------------------------------------------------------------------------
// Cardio
// ---------------------------------------------------------------------------

export function validarCardio(
  r: Pick<RegistroCardio, 'duracion_seg' | 'distancia_km' | 'calorias' | 'nivel_resistencia'>,
): Resultado {
  const h: Hallazgo[] = [];

  if (!Number.isFinite(r.duracion_seg) || r.duracion_seg <= 0) {
    h.push({ campo: 'duracion_seg', severidad: 'error', codigo: 'duracion_invalida', mensaje: 'La duración tiene que ser mayor que cero.' });
    return resultado(h);
  }
  if (r.duracion_seg > 6 * 3600) {
    h.push({ campo: 'duracion_seg', severidad: 'confirmar', codigo: 'duracion_larga', mensaje: 'Más de 6 horas de cardio: ¿está bien leído el tiempo?' });
  }

  const minutos = r.duracion_seg / 60;
  if (Number.isFinite(r.calorias) && r.calorias > 0) {
    const calPorMin = r.calorias / minutos;
    if (calPorMin > LIMITES.caloriasPorMinuto) {
      h.push({
        campo: 'calorias',
        severidad: 'error',
        codigo: 'calorias_por_minuto',
        mensaje: `${calPorMin.toFixed(1)} cal/min es imposible: la consola se leyó mal (máximo aceptado ${LIMITES.caloriasPorMinuto}).`,
      });
    }
  }

  if (Number.isFinite(r.distancia_km) && r.distancia_km > 0) {
    const kmh = r.distancia_km / (r.duracion_seg / 3600);
    if (kmh > LIMITES.velocidadKmhMax) {
      h.push({
        campo: 'distancia_km',
        severidad: 'error',
        codigo: 'velocidad_implausible',
        mensaje: `${kmh.toFixed(1)} km/h no es plausible: distancia y tiempo no cierran.`,
      });
    } else if (kmh > 22) {
      h.push({
        campo: 'distancia_km',
        severidad: 'confirmar',
        codigo: 'velocidad_alta',
        mensaje: `${kmh.toFixed(1)} km/h es muy rápido para cinta o elíptica. ¿Era bicicleta?`,
      });
    } else if (kmh < 0.5) {
      h.push({
        campo: 'distancia_km',
        severidad: 'confirmar',
        codigo: 'velocidad_baja',
        mensaje: `${kmh.toFixed(2)} km/h es sospechosamente lento: puede faltar un dígito en la distancia.`,
      });
    }
  }

  if (r.nivel_resistencia === null) {
    // Aviso, no error: el dato se guarda igual, pero la sesión queda marcada como no
    // comparable (§5.4). Perder el registro por un dato que no sale en la foto sería peor.
    h.push({
      campo: 'nivel_resistencia',
      severidad: 'aviso',
      codigo: 'sin_nivel',
      mensaje: 'Sin nivel de resistencia esta sesión no es comparable con las otras.',
    });
  }

  return resultado(h);
}

// ---------------------------------------------------------------------------
// Medidas corporales
// ---------------------------------------------------------------------------

type MedidaValidable = Pick<
  MedidaCorporal,
  'peso' | 'unidad_peso' | 'grasa_pct' | 'masa_magra' | 'masa_muscular' | 'masa_osea' | 'agua_pct' | 'grasa_visceral' | 'imc' | 'tmb'
>;

function rango(
  h: Hallazgo[],
  campo: string,
  valor: number | null,
  limites: { min: number; max: number },
  severidad: Severidad,
  unidad = '',
): void {
  if (valor === null || !Number.isFinite(valor)) return;
  if (valor < limites.min || valor > limites.max) {
    h.push({
      campo,
      severidad,
      codigo: `${campo}_fuera_de_rango`,
      mensaje: `${campo} = ${valor}${unidad} está fuera del rango esperado (${limites.min}–${limites.max}${unidad}).`,
    });
  }
}

/**
 * `previa` es la lectura anterior, para detectar el salto de más de 3 kg de §8. Es lo que
 * distingue "hoy pesé 9 kg menos" (un dígito mal leído) de una pérdida real.
 */
export function validarMedida(m: MedidaValidable, previa?: MedidaValidable | null): Resultado {
  const h: Hallazgo[] = [];
  const pesoKg = enUnidad({ valor: m.peso, unidad: m.unidad_peso }, 'kg');

  if (!Number.isFinite(m.peso) || m.peso <= 0) {
    h.push({ campo: 'peso', severidad: 'error', codigo: 'peso_invalido', mensaje: 'El peso tiene que ser mayor que cero.' });
    return resultado(h);
  }
  rango(h, 'peso', pesoKg, LIMITES.pesoCorporalKg, 'error', ' kg');
  rango(h, 'grasa_pct', m.grasa_pct, LIMITES.grasaPct, 'error', ' %');
  rango(h, 'agua_pct', m.agua_pct, LIMITES.aguaPct, 'confirmar', ' %');
  rango(h, 'grasa_visceral', m.grasa_visceral, LIMITES.grasaVisceral, 'confirmar');
  rango(h, 'imc', m.imc, LIMITES.imc, 'confirmar');
  rango(h, 'tmb', m.tmb, LIMITES.tmb, 'confirmar', ' kcal');

  if (previa) {
    const previoKg = enUnidad({ valor: previa.peso, unidad: previa.unidad_peso }, 'kg');
    const delta = Math.abs(pesoKg - previoKg);
    if (delta > LIMITES.deltaPesoKg) {
      h.push({
        campo: 'peso',
        severidad: 'confirmar',
        codigo: 'salto_de_peso',
        mensaje: `${delta.toFixed(1)} kg de diferencia con la lectura anterior. Si es correcto, confirmalo; si no, revisá el número.`,
      });
    }
  }

  // §8 — masa magra + masa grasa ≈ peso, tolerancia 2 %.
  const magraKg = m.masa_magra === null ? null : enUnidad({ valor: m.masa_magra, unidad: m.unidad_peso }, 'kg');
  const grasaKg = m.grasa_pct === null ? null : (pesoKg * m.grasa_pct) / 100;

  if (magraKg !== null && grasaKg !== null) {
    const suma = magraKg + grasaKg;
    const desvio = Math.abs(suma - pesoKg) / pesoKg;
    if (desvio > 0.1) {
      h.push({
        campo: 'masa_magra',
        severidad: 'error',
        codigo: 'composicion_incoherente',
        mensaje: `Masa magra + masa grasa = ${suma.toFixed(1)} kg, pero el peso es ${pesoKg.toFixed(1)} kg (${(desvio * 100).toFixed(0)} % de desvío). Algún número se leyó mal.`,
      });
    } else if (desvio > LIMITES.toleranciaComposicion) {
      h.push({
        campo: 'masa_magra',
        severidad: 'confirmar',
        codigo: 'composicion_al_limite',
        mensaje: `Masa magra + masa grasa = ${suma.toFixed(1)} kg contra ${pesoKg.toFixed(1)} kg de peso: ${(desvio * 100).toFixed(1)} % de desvío, por encima del 2 % esperado.`,
      });
    }
  }

  if (magraKg !== null && magraKg > pesoKg) {
    h.push({
      campo: 'masa_magra',
      severidad: 'error',
      codigo: 'magra_mayor_que_peso',
      mensaje: 'La masa magra no puede ser mayor que el peso total.',
    });
  }

  const muscularKg = m.masa_muscular === null ? null : enUnidad({ valor: m.masa_muscular, unidad: m.unidad_peso }, 'kg');
  if (muscularKg !== null && magraKg !== null && muscularKg > magraKg + 1e-6) {
    h.push({
      campo: 'masa_muscular',
      severidad: 'confirmar',
      codigo: 'muscular_mayor_que_magra',
      mensaje: 'La masa muscular no debería superar a la masa magra (que la incluye, más hueso y agua).',
    });
  }

  return resultado(h);
}
